import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Cookies from 'universal-cookie';

import PropTypes from 'prop-types';
import moment from 'moment';
import qs from 'query-string';
import isEqual from 'lodash.isequal';
import { filtersMeta, getVisibleFiltersMeta, WORKLIST_COLUMNS_KEY } from './filtersMeta';
import StatusBadge from './StatusBadge';
import HttpClient from '@utils/HttpClient';
import { reportIdToDateTime } from '@utils/datetime';
import { updateMenuOptions } from '@utils/navigation';
import { escapeText } from '@utils/validation';

import { clearStudiesStorage, setStudiesStorage, useAppConfig, } from '@state';
import { useDebounce, useSearchParams } from '@hooks';
import { hotkeys, ServicesManager } from '@ohif/core';
import i18n from '@ohif/i18n';
import {
  Button,
  ButtonEnums,
  EmptyStudies,
  Header,
  Icon,
  InvestigationalUseDialog,
  LoadingIndicatorProgress,
  StudyListExpandedRow,
  StudyListFilter,
  StudyListPagination,
  StudyListTable,
  TooltipClipboard,
  useModal,
  UserPreferences,
  useSessionStorage,
} from '@ohif/ui';
import Footer from '../../components/Footer';
import SvgLoading from '../../components/SvgLoading';

const { availableLanguages, defaultLanguage, currentLanguage } = i18n;


function goToStudy(navigate, srvcName, reportId, sortedStudies, workspaceId) {
  setStudiesStorage(workspaceId, sortedStudies);
  navigate(`viewer/dicomjson?url=${srvcName},${reportId}&workspace=${workspaceId}`);
}

function verifyWorkspace(workspaceId, workspaces) {
  return workspaces.find(workspace => workspace.id === workspaceId);
}

function _tryParseInt(str, defaultValue) {
  let retValue = defaultValue;
  if (str && str.length > 0) {
    if (!isNaN(str)) {
      retValue = parseInt(str);
    }
  }
  return retValue;
}

function _getQueryFilterValues(params) {
  const queryFilterValues = {
    bodyPart: params.get('bodyPart'),
    patientAge: params.get('patientAge'),
    patientName: params.get('patientName'),
    patientSex: params.get('patientSex'),
    institutionName: params.get('institutionName'),
    accessionType: params.get('accessionType') || 'accession',
    mrn: params.get('mrn'),
    studyDate: {
      startDate: params.get('startDate') || null,
      endDate: params.get('endDate') || null,
    },
    dateType: params.get('dateType') || 'studyDate',
    description: params.get('description'),
    modalities: params.get('modalities') ? params.get('modalities').split(',') : [],
    accession: params.get('accession'),
    jobStatus: params.get('jobStatus') ? params.get('jobStatus').split(',') : [],
    sortBy: params.get('sortby'),
    sortDirection: params.get('sortdirection'),
    pageNumber: _tryParseInt(params.get('pagenumber'), undefined),
    resultsPerPage: _tryParseInt(params.get('resultsperpage'), undefined),
    datasources: params.get('datasources'),
    configUrl: params.get('configurl'),
    workspace: params.get('workspace'),
  };

  Object.keys(queryFilterValues).forEach(
    key => queryFilterValues[key] == null && delete queryFilterValues[key]
  );

  return queryFilterValues;
}

function _sortProcessedAt(s1, s2, sortModifier) {
  const d1 = s1.reportId ? (s1.reportId.match(/(\d{8}T\d{6})/) || [''])[0] : '';
  const d2 = s2.reportId ? (s2.reportId.match(/(\d{8}T\d{6})/) || [''])[0] : '';
  if (d1 && d2) {
    return (d1 > d2 ? 1 : -1) * sortModifier;
  } else if (d1) {
    return sortModifier;
  } else if (d2) {
    return -1 * sortModifier;
  }
  return 0;
}

function _sortStringDates(s1, s2, sortModifier) {
  const s1Time = Math.floor(s1.time || '000000').toString().padStart(6, '0');
  const s1Date = moment(s1.date + ' ' + s1Time, ['YYYYMMDD HHmmss', 'YYYY.MM.DD HHmmss'], true);
  const s2Time = Math.floor(s2.time || '000000').toString().padStart(6, '0');
  const s2Date = moment(s2.date + ' ' + s2Time, ['YYYYMMDD HHmmss', 'YYYY.MM.DD HHmmss'], true);

  if (s1Date.isValid() && s2Date.isValid()) {
    return (s1Date.toISOString() > s2Date.toISOString() ? 1 : -1) * sortModifier;
  } else if (s1Date.isValid()) {
    return sortModifier;
  } else if (s2Date.isValid()) {
    return -1 * sortModifier;
  }
}

const defaultFilterValues = {
  bodyPart: '',
  patientAge: '',
  patientName: '',
  patientSex: '',
  institutionName: '',
  accessionType: 'accession',
  mrn: '',
  studyDate: {
    startDate: null,
    endDate: null,
  },
  dateType: 'studyDate',
  description: '',
  modalities: [],
  accession: '',
  sortBy: '',
  workspace: '',
  sortDirection: 'none',
  pageNumber: 1,
  resultsPerPage: 25,
  datasources: '',
  configUrl: null,
  jobStatus: [],
};

function WorkList({
  data: studies,
  dataTotal: studiesTotal,
  isLoadingData,
  setIsLoading,
  init,
  dataSource,
  hotkeysManager,
  dataPath,
  onRefresh,
  servicesManager,
  workspaces,
  setData
}) {
  const cookies = new Cookies(null, { path: '/' });
  const { hotkeyDefinitions, hotkeyDefaults } = hotkeysManager;
  const { show, hide } = useModal();
  const { t } = useTranslation();
  const location = useLocation();
  // ~ Modes
  const { appConfig, setAppConfig, filteredStudies, setAppliedFilters, setFilteredStudies, setFilterApplied } = useAppConfig();
  const { isStaff } = appConfig.session;
  const [services, setServices] = useState([]);
  const [visibleFiltersMeta, setVisibleFiltersMeta] = useState(() => getVisibleFiltersMeta());
  // ~ Filters
  const searchParams = useSearchParams();
  const navigate = useNavigate();
  const queryFilterValues = useMemo(() => _getQueryFilterValues(searchParams), [searchParams]);
  const [sessionQueryFilterValues, updateSessionQueryFilterValues] = useSessionStorage({
    key: 'queryFilterValues',
    defaultValue: queryFilterValues,
    clearOnUnload: true,
  });
  const [filterValues, _setFilterValues] = useState({
    ...defaultFilterValues,
    ...sessionQueryFilterValues,
  });

  const debouncedFilterValues = useDebounce(filterValues, 200);
  const { resultsPerPage, pageNumber, sortBy, sortDirection } = filterValues;

  const canSort = true;
  const shouldUseDefaultSort = sortBy === '' || !sortBy;
  const sortModifier = sortDirection === 'descending' ? 1 : -1;
  const defaultSortValues = useMemo(
    () => shouldUseDefaultSort && canSort ? { sortBy: 'studyDate', sortDirection: 'ascending' } : {},
    [shouldUseDefaultSort, canSort]
  );

  const sortedStudies = useMemo(() => {
    let result = studies;

    if (filteredStudies === null) {
      return [];
    }

    if (filteredStudies.length) {
      result = result.filter(item => filteredStudies.includes(item.reportId));
    }

    if (canSort) {
      const studiesCopy = [...result];
      studiesCopy.sort((s1, s2) => {
        if (shouldUseDefaultSort) {
          const ascendingSortModifier = -1;
          if (filterValues.dateType === 'processedAt') {
            return _sortProcessedAt(s1, s2, ascendingSortModifier);
          }
          return _sortStringDates(s1, s2, ascendingSortModifier);
        }

        const s1Prop = s1[sortBy];
        const s2Prop = s2[sortBy];

        if (typeof s1Prop === 'string' && typeof s2Prop === 'string') {
          return s1Prop.localeCompare(s2Prop) * sortModifier;
        } else if (typeof s1Prop === 'number' && typeof s2Prop === 'number') {
          return (s1Prop > s2Prop ? 1 : -1) * sortModifier;
        } else if (!s1Prop && s2Prop) {
          return -1 * sortModifier;
        } else if (!s2Prop && s1Prop) {
          return 1 * sortModifier;
        } else if (sortBy === 'studyDate') {
          if (filterValues.dateType === 'processedAt') {
            return _sortProcessedAt(s1, s2, sortModifier);
          }
          return _sortStringDates(s1, s2, sortModifier);
        }

        return 0;
      });
      return studiesCopy;
    }

    return result;
  }, [studies, filteredStudies, canSort, shouldUseDefaultSort, sortBy, sortDirection, sortModifier, filterValues.dateType, filterValues.accessionType]);

  const [expandedRows, setExpandedRows] = useState([]);
  const [loadedFindings, setLoadedFindings] = useState({});
  const [loadingFindings, setLoadingFindings] = useState({});

  const numOfStudies = useMemo(
    () => sortedStudies?.length || 0,
    [sortedStudies, studiesTotal]
  );

  const querying = useMemo(() => {
    return isLoadingData || expandedRows.length > 0;
  }, [isLoadingData, expandedRows.length]);

  const setFilterValues = useCallback((val) => {
    if (filterValues.pageNumber === val.pageNumber) {
      val.pageNumber = 1;
    }
    _setFilterValues(val);
    updateSessionQueryFilterValues(val);
    setExpandedRows([]);
  }, [filterValues.pageNumber, updateSessionQueryFilterValues]);

  const onPageNumberChange = useCallback((newPageNumber) => {
    const oldPageNumber = filterValues.pageNumber;
    const isNextPage = newPageNumber > oldPageNumber;
    const hasNextPage = (newPageNumber - 1) * resultsPerPage < numOfStudies;

    if (isNextPage && !hasNextPage) {
      return;
    }

    setFilterValues({ ...filterValues, pageNumber: newPageNumber });
  }, [filterValues, resultsPerPage, numOfStudies, setFilterValues]);

  const onResultsPerPageChange = useCallback((newResultsPerPage) => {
    setFilterValues({
      ...filterValues,
      pageNumber: 1,
      resultsPerPage: Number(newResultsPerPage),
    });
  }, [filterValues, setFilterValues]);

  const toggleChecked = useCallback(async (event, reportId, srvcName) => {
    event.stopPropagation();
    const formData = {
      srvc_name: srvcName,
      is_checked: event.target.checked
    };
    await HttpClient.patch(
      `/dicomweb/studies/${reportId}/toggle_check`,
      formData,
      cookies.get('token')
    );
  }, [cookies]);

  const loadExamFindings = useCallback(async (reportId, srvcName) => {
    const resp = await HttpClient.get(`/dicomweb/studies/${reportId}?srvc_name=${srvcName}`);
    return resp.data;
  }, []);

  const canUpload = useCallback(() => {
    return services?.some(service => service.can_upload);
  }, [services]);

  const getServices = useCallback((workspace, currentFilter = filterValues) => {
    if (workspace.id === currentFilter.workspace)
      return;
    HttpClient.get(`/services?workspace=${workspace.id}`)
      .then(res => setServices(res.data))
      .catch(err => console.log(err));
  }, [filterValues]);

  useEffect(() => {
    setAppConfig({ ...appConfig, session: { ...appConfig.session, isStaff } });
  }, []);

  // Sync URL query parameters with filters
  useEffect(() => {
    if (queryFilterValues.workspace && isLoadingData && workspaces.length) {
      if (!verifyWorkspace(queryFilterValues.workspace, workspaces)) {
        toast.error('NotFound:Workspace not found');
        let firstWorkspace = workspaces.filter(w => w.is_default);
        firstWorkspace = firstWorkspace.length ? firstWorkspace[0] : workspaces[0];
        setData(currentData => ({ ...currentData, allStudies: null, selectedWorkspace: firstWorkspace }));
        setFilterValues({ ...filterValues, workspace: firstWorkspace.id });
        setAppliedFilters({ srvc_name: 'cxr' });
        setFilteredStudies([]);
        getServices(firstWorkspace, {});
      } else {
        getServices({ id: queryFilterValues.workspace }, {});
      }
    } else if (workspaces.length && !filterValues.workspace) {
      let firstWorkspace = workspaces.filter(w => w.is_default);
      firstWorkspace = firstWorkspace.length ? firstWorkspace[0] : workspaces[0];
      getServices(firstWorkspace);
      setFilterValues({ ...filterValues, workspace: firstWorkspace.id });
    }

    if (!debouncedFilterValues) {
      return;
    }

    const queryString = {};
    Object.keys(defaultFilterValues).forEach(key => {
      const defaultValue = defaultFilterValues[key];
      const currValue = debouncedFilterValues[key];

      if (key === 'studyDate') {
        if (currValue.startDate && defaultValue.startDate !== currValue.startDate) {
          queryString.startDate = currValue.startDate;
        }
        if (currValue.endDate && defaultValue.endDate !== currValue.endDate) {
          queryString.endDate = currValue.endDate;
        }
      } else if (key === 'modalities' && currValue.length) {
        queryString.modalities = currValue.join(',');
      } else if (key === 'jobStatus' && currValue.length) {
        queryString.jobStatus = currValue.join(',');
      } else if (currValue !== defaultValue) {
        queryString[key] = currValue;
      }
    });

    const search = qs.stringify(queryString, {
      skipNull: true,
      skipEmptyString: true,
    });

    navigate({ pathname: '/', search: search ? `?${search}` : undefined }, { replace: true });
  }, [debouncedFilterValues, workspaces]);

  const isFiltering = useCallback((filterValues, defaultFilterValues) => {
    return !isEqual(filterValues, defaultFilterValues);
  }, []);

  const paginationValues = useMemo(() => {
    const rollingPageNumber = pageNumber - 1;
    const offset = resultsPerPage * rollingPageNumber;
    const offsetAndTake = offset + resultsPerPage;

    return { offset, offsetAndTake };
  }, [resultsPerPage, pageNumber]);

  const { offset, offsetAndTake } = paginationValues;

  const tableDataSource = useMemo(() => {
    return sortedStudies.map((study, key) => {
      const {
        accession,
        institutionName,
        bodyPart,
        date,
        description,
        errors: rawErrors = [],
        indications: rawIndications = [],
        instances,
        isChecked,
        jobStatus,
        modalities,
        mrn,
        patientAge,
        patientName,
        patientSex,
        quality: rawQuality = [],
        reportId,
        srvcName,
        studyInstanceUid,
        time,
      } = study;

      const studyFindings = loadedFindings[reportId];
      const errors = studyFindings?.errors ?? rawErrors;
      const indications = studyFindings?.indications ?? rawIndications;
      const quality = studyFindings?.quality ?? rawQuality;
      const isLoadingFindings = Boolean(loadingFindings[reportId]);

      const isExpanded = expandedRows.some(k => k === reportId);
      const studyDate =
        date &&
        moment(date, ['YYYYMMDD', 'YYYY.MM.DD'], true).isValid() &&
        moment(date, ['YYYYMMDD', 'YYYY.MM.DD']).format(t('Common:localDateFormat', 'DD/MM/YYYY'));
      const studyTime =
        time &&
        moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).isValid() &&
        moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).format(
          t('Common:localTimeFormat', 'HH:mm')
        );

      return {
        reportId,
        dataCY: `studyRow-${studyInstanceUid}`,
        studyData: { isChecked },
        row: (() => {
          const availableCellsMap = {
            patientName: {
              key: 'patientName',
              content: patientName ? (
                <TooltipClipboard>{patientName}</TooltipClipboard>
              ) : (
                <span className="text-gray-700">(Empty)</span>
              ),
            },
            institutionName: {
              key: 'institutionName',
              content: institutionName,
            },
            mrn: {
              key: 'mrn',
              content: mrn,
            },
            patientAge: {
              key: 'patientAge',
              content: patientAge,
            },
            patientSex: {
              key: 'patientSex',
              content: patientSex,
            },
            accession: {
              key: 'accession',
              content: (
                <TooltipClipboard text={isStaff ? reportId : accession}>
                  <span>
                    {accession}
                    {isStaff && !accession ? '---' : null}
                  </span>
                </TooltipClipboard>
              ),
              gridCol: 3,
            },
            studyDate: {
              key: 'studyDate',
              content: (
                <div className="flex flex-col">
                  {filterValues.dateType === 'processedAt' ? (
                    (() => {
                      const dt = reportIdToDateTime(reportId);
                      if (!dt) return <span className="text-gray-700">(Empty)</span>;
                      const [pDate, pTime] = dt.split(' ');
                      return (
                        <>
                          {pDate && <span>{pDate}</span>}
                          {pTime && <span className="text-xs">{pTime}</span>}
                        </>
                      );
                    })()
                  ) : (
                    <>
                      {studyDate && <span>{studyDate}</span>}
                      {studyTime && <span className="text-xs">{studyTime}</span>}
                    </>
                  )}
                </div>
              ),
              title:
                filterValues.dateType === 'processedAt'
                  ? reportIdToDateTime(reportId) || ''
                  : `${studyDate || ''} ${studyTime || ''}`,
            },
            description: {
              key: 'description',
              content: <TooltipClipboard><span className="text-sm">{description}</span></TooltipClipboard>,
            },
            modalities: {
              key: 'modality',
              content: modalities,
              title: modalities,
            },
            bodyPart: {
              key: 'bodyPart',
              content: bodyPart,
            },
            jobStatus: {
              key: 'jobStatus',
              content: <StatusBadge status={jobStatus} />,
            },
            instances: {
              key: 'instances',
              content: (
                <button
                  className="w-full h-full flex items-center justify-center"
                  onClick={async event => {
                    event.stopPropagation();
                    if (!isExpanded) {
                      setExpandedRows(s => (s.includes(reportId) ? s : [...s, reportId]));
                      const isAlreadyLoaded = Boolean(loadedFindings[reportId] || study.hasLoadedFindings);
                      const isProcessing = Boolean(jobStatus?.includes('Processing'));
                      if (!isAlreadyLoaded && !isProcessing) {
                        setLoadingFindings(prev => ({ ...prev, [reportId]: true }));
                        try {
                          const resp = await loadExamFindings(reportId, srvcName);
                          if (resp) {
                            const updatedData = { ...resp, hasLoadedFindings: true };
                            setLoadedFindings(prev => ({ ...prev, [reportId]: updatedData }));
                            Object.assign(study, updatedData);
                            if (setData) {
                              setData(currentData => {
                                if (!currentData?.studies) return currentData;
                                return {
                                  ...currentData,
                                  studies: currentData.studies.map(s =>
                                    s.reportId === reportId ? { ...s, ...updatedData } : s
                                  ),
                                  allStudies: currentData.allStudies
                                    ? currentData.allStudies.map(s =>
                                        s.reportId === reportId ? { ...s, ...updatedData } : s
                                      )
                                    : currentData.allStudies,
                                };
                              });
                            }
                          }
                        } catch (error) {
                          console.error('Error loading exam findings:', error);
                        } finally {
                          setLoadingFindings(prev => {
                            const next = { ...prev };
                            delete next[reportId];
                            return next;
                          });
                        }
                      }
                    } else {
                      setExpandedRows(s => s.filter(n => reportId !== n));
                    }
                  }}
                >
                  <>
                    <Icon name="group-layers" className="mr-1 inline-flex w-4 text-primary-active" />
                    {instances}
                    <Icon
                      name="chevron-down"
                      className={`inline-flex w-4 ml-3 transition-transform duration-300 ${
                        isExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </>
                </button>
              ),
              title: (instances || 0).toString(),
            },
          };

          return visibleFiltersMeta
            .map(col => {
              const cell = availableCellsMap[col.name];
              if (!cell) return null;
              return {
                ...cell,
                gridCol: col.gridCol,
              };
            })
            .filter(Boolean);
        })(),
        expandedContent: (
          <StudyListExpandedRow>
            <div
              className="flex flex-row gap-2"
              style={{
                animation: 'expandIn 300ms cubic-bezier(0.16, 1, 0.3, 1) forwards',
              }}
            >
              <style>{`
                @keyframes expandIn {
                  from {
                    opacity: 0;
                    transform: translateY(-8px);
                  }
                  to {
                    opacity: 1;
                    transform: translateY(0);
                  }
                }
              `}</style>
              <div className="flex flex-col gap-2">
                {isStaff && (
                  <Button
                    type={ButtonEnums.type.primary}
                    size={ButtonEnums.size.medium}
                    onClick={() => window.open(`${process.env.API_URL}/search-exams/?q=${reportId}`, '_blank').focus()}
                  >
                    <Icon className="h-4 w-4 text-black" name="launch-arrow" />
                    {t('Header:See in staff')}
                  </Button>
                )}

                {jobStatus.includes('Viewer OK') && (
                  <Button
                    type={ButtonEnums.type.primary}
                    size={ButtonEnums.size.medium}
                    onClick={() => goToStudy(navigate, srvcName, reportId, sortedStudies, queryFilterValues.workspace)}
                  >
                    <Icon className="h-4 w-4 text-black" name="launch-arrow" />
                    {t('Common:See Study')}
                  </Button>
                )}
              </div>

              <div className="w-full ml-10 flex flex-row flex-wrap gap-5">
                {Boolean(jobStatus?.includes('Processing')) && (
                  <span className="self-center w-full font-bold text-primary-light text-center text-sm">{t('Common:The exam is being processed')}</span>
                )}
                {isLoadingFindings ? (
                  <div className="flex flex-[0.8] w-full justify-center items-center gap-2 text-primary-light text-sm py-4">
                    <SvgLoading className="w-8 h-8 text-primary-light animate-spin" />
                  </div>
                ) : (
                  <>
                    {errors.length ? (
                      <div className="w-full">
                        <span className="font-bold text-red-400">{t('ProcessingErrors:Error')}</span>
                        <ul className="list-disc text-sm">
                          {errors.map((error, index) => {
                            return <li key={reportId + error + index}>{t(`ProcessingErrors:${error}`)}</li>;
                          })}
                        </ul>
                      </div>
                    ) : null}

                    {indications.length ? (
                      <div className="flex-1 flex-grow-1">
                        <span className="font-bold text-primary-light">{t('StudyList:Indications')}</span>
                        <ul className="list-disc text-sm">
                          {!indications.length && <li>{t('StudyList:No indications')}</li>}
                          {indications.map((indication, index) => {
                            return <li key={indication + index}>{escapeText(indication)}</li>;
                          })}
                        </ul>
                      </div>
                    ) : null}

                    {quality.length ? (
                      <div className="flex-1 flex-grow-1">
                        <span className="font-bold text-primary-light">{t('StudyList:Quality')}</span>
                        <ul className="list-disc text-sm">
                          {quality.map((indication, index) => {
                            return <li key={indication + index}>{escapeText(indication)}</li>;
                          })}
                        </ul>
                      </div>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </StudyListExpandedRow>
        ),
        onClickRow: () => {
          jobStatus.includes('Viewer OK') ? goToStudy(navigate, srvcName, reportId, sortedStudies, queryFilterValues.workspace) : null;
        },
        toggleChecked: (event) => toggleChecked(event, reportId, srvcName),
        isExpanded,
      };
    });
  }, [sortedStudies, expandedRows, loadedFindings, loadingFindings, isStaff, t, queryFilterValues.workspace, navigate, toggleChecked, loadExamFindings, setData, filterValues.accessionType, filterValues.dateType, visibleFiltersMeta]);

  const hasStudies = numOfStudies > 0;

  const menuOptions = useMemo(() => {
    const options = [
      {
        title: t('Header:Preferences'),
        icon: 'settings',
        onClick: () =>
          show({
            title: t('UserPreferencesModal:User preferences'),
            content: UserPreferences,
            contentProps: {
              hotkeyDefaults: hotkeysManager.getValidHotkeyDefinitions(hotkeyDefaults),
              hotkeyDefinitions: null,
              currentLanguage: currentLanguage(),
              availableLanguages,
              defaultLanguage,
              isWorklist: true,
              allColumns: filtersMeta,
              onCancel: () => {
                hotkeys.stopRecord();
                hotkeys.unpause();
                hide();
              },
              onSubmit: ({ hotkeyDefinitions, language, visibleColumns }) => {
                if (language && language.value !== currentLanguage().value) {
                  i18n.changeLanguage(language.value);
                }
                if (hotkeyDefinitions) {
                  hotkeysManager.setHotkeys(hotkeyDefinitions);
                }
                if (visibleColumns && Array.isArray(visibleColumns)) {
                  localStorage.setItem(WORKLIST_COLUMNS_KEY, JSON.stringify(visibleColumns));
                  setVisibleFiltersMeta(getVisibleFiltersMeta());
                }
                hide();
              },
              onReset: () => {
                hotkeysManager.restoreDefaultBindings();
                localStorage.removeItem(WORKLIST_COLUMNS_KEY);
                setVisibleFiltersMeta(getVisibleFiltersMeta());
              },
              hotkeysModule: hotkeys,
            },
          }),
      },
      {
        icon: 'power-off',
        title: t('Header:Logout'),
        onClick: () => {
          toast.promise(
            HttpClient.get('/logout/'),
            {
              loading: t('Header:Disconnecting'),
              success: () => {
                navigate('/login', { replace: true });
                clearStudiesStorage();
                return t('Header:Logout success');
              },
              error: t('Header:Logout error'),
            },
          );
        },
      },
    ];

    if (isStaff) {
      updateMenuOptions(options, navigate, t);
    }

    return options;
  }, [t, show, hide, isStaff, navigate, hotkeysManager, hotkeyDefaults]);

  const workspacesOptions = useMemo(() => {
    return workspaces.map(workspace => ({
      icon: workspace.is_default ? 'favorite' : 'favorite-white',
      id: workspace.id,
      title: workspace.name,
      titleIcon: t('Header:Set as default'),
      onClick: () => {
        const currentFilter = { ...filterValues };
        setData(currentData => ({ ...currentData, allStudies: null, selectedWorkspace: workspace }));
        setFilterValues({ ...defaultFilterValues, workspace: workspace.id });
        // setFilterValues({ ...filterValues, workspace: workspace.id });
        setAppliedFilters({ srvc_name: 'cxr' });
        getServices(workspace, currentFilter);
      },
      onClickIcon: async (e) => {
        const isDefault = !workspace.is_default;
        await HttpClient.patch(
          `/workspaces/${workspace.id}/default_workspace`,
          { isDefault },
          cookies.get('token')
        );
        const newWorkspaces = workspaces.map(obj => {
          if (obj.id === workspace.id) {
            return { ...obj, is_default: !workspace.is_default };
          } else {
            return { ...obj, is_default: false };
          }
        });
        setData(current => ({ ...current, workspaces: newWorkspaces }));
        toast.success(t(`Header:Workspace ${isDefault ? 'set as default' : 'unset as default'}`));
      }
    }));
  }, [workspaces, t, filterValues, setData, setFilterValues, setAppliedFilters, setFilteredStudies, setFilterApplied, getServices, cookies]);

  const { customizationService } = servicesManager.services;
  const { component: dicomUploadComponent } =
    // customizationService.get(isStaff ? 'multiDicomUploadComponent' : 'dicomUploadComponent') ?? {};
    customizationService.get('dicomUploadComponent') ?? {};

  const uploadProps = useMemo(() => {
    if (dicomUploadComponent && dataSource.getConfig()?.dicomUploadEnabled && canUpload()) {
      return {
        title: t('Upload:Upload files'),
        closeButton: true,
        shouldCloseOnEsc: true,
        shouldCloseOnOverlayClick: true,
        fullsize: true,
        content: dicomUploadComponent.bind(null, {
          cookies,
          services: services.filter(service => service.can_upload),
          workspace: filterValues.workspace,
          dataSource,
          onComplete: () => {
            hide();
            onRefresh();
          },
          onStarted: () => {
            show({
              ...uploadProps,
              closeButton: true,
            });
          },
        }),
      };
    }
    return undefined;
  }, [dicomUploadComponent, dataSource, canUpload, t, cookies, services, filterValues.workspace, hide, onRefresh, show]);

  const { component: dataSourceConfigurationComponent } =
    customizationService.get('ohif.dataSourceConfigurationComponent') ?? {};

  const tableDataSourceSlice = useMemo(
    () => tableDataSource.slice(offset, offsetAndTake),
    [tableDataSource, offset, offsetAndTake]
  );

  const handleClearFilters = useCallback(() => {
    _setFilterValues(current => ({
      ...defaultFilterValues,
      workspace: current.workspace,
      dateType: current.dateType,
      accessionType: current.accessionType,
    }));
    setAppliedFilters({ srvc_name: 'cxr' });
    setFilteredStudies([]);
  }, [setAppliedFilters, setFilteredStudies]);

  const handleUploadClick = useCallback(() => {
    if (uploadProps) {
      show(uploadProps);
    }
  }, [uploadProps, show]);

  const handleRefresh = useCallback(() => {
    setData(currentData => ({ ...currentData, allStudies: null }));
  }, [setData]);

  if (init) {
    document.title = `HarpiAI APP | ${t('Common:Loading')}`;
    return <LoadingIndicatorProgress className="h-full w-full bg-black" />;
  } else {
    document.title = 'HarpiAI APP | Worklist';
  }

  return (
    <div className="flex h-screen flex-col bg-black ">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
        workspacesOptions={workspacesOptions}
      />

      <InvestigationalUseDialog dialogConfiguration={{ option: 'never' }} />

      <div className="ohif-scrollbar flex grow flex-col overflow-y-auto overflow-x-auto">
        <div
          className={`w-full max-w-[1420px] mx-auto px-4 flex flex-col grow ${
            visibleFiltersMeta.length <= 4 ? 'min-w-[640px]' : 'min-w-[1024px]'
          }`}
        >
          <StudyListFilter
            numOfStudies={numOfStudies}
            filtersMeta={visibleFiltersMeta}
            filterValues={{ ...filterValues, ...defaultSortValues }}
            onChange={setFilterValues}
            clearFilters={handleClearFilters}
            isFiltering={isFiltering(filterValues, defaultFilterValues)}
            onUploadClick={uploadProps ? handleUploadClick : undefined}
            getDataSourceConfigurationComponent={
              dataSourceConfigurationComponent ? () => dataSourceConfigurationComponent() : undefined
            }
            isLoadingData={isLoadingData}
            setIsLoading={setIsLoading}
            refresh={handleRefresh}
        />

        <div className="flex flex-col relative min-h-[60vh]">
          {appConfig.showLoadingIndicator && isLoadingData && (
            <LoadingIndicatorProgress
              className="w-full rounded-xl"
              style={{
                position: 'fixed',
                background: 'rgba(5, 8, 16, 0.7)',
                backdropFilter: 'blur(4px)',
                zIndex: 100
              }}
            />
          )}

          {workspaces.length > 0 ? (
            <>
              <StudyListTable
                tableDataSource={tableDataSourceSlice}
                numOfStudies={numOfStudies}
                querying={querying}
                filtersMeta={visibleFiltersMeta}
              />

              {hasStudies && filteredStudies !== null ? (
                <div className="grow mb-10">
                  <StudyListPagination
                    onChangePage={onPageNumberChange}
                    onChangePerPage={onResultsPerPageChange}
                    currentPage={pageNumber}
                    perPage={resultsPerPage}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center pt-24 pb-16">
                  {!isLoadingData && <EmptyStudies />}
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center pt-48 pb-16">
              {!isLoadingData && <EmptyStudies />}
            </div>
          )}
        </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

WorkList.propTypes = {
  data: PropTypes.array.isRequired,
  dataSource: PropTypes.shape({
    query: PropTypes.object.isRequired,
    getConfig: PropTypes.func,
  }).isRequired,
  isLoadingData: PropTypes.bool.isRequired,
  servicesManager: PropTypes.instanceOf(ServicesManager),
};

export default WorkList;
